/* eslint-disable max-lines */
import { CameraIcon } from '@heroicons/react/24/outline';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import { useCallback, useId } from 'react';
import clsx from 'clsx';
import { resizeImageToWidth } from './imageResizer';

interface Props {
  imagePreview: string | null;
  onImageChange: (file: File) => void;
  onImageRemove?: () => void;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  accept?: string;
  className?: string;
  error?: boolean;
  errorText?: string;
}

export function ImageUpload(props: Props) {
  const {
    imagePreview,
    onImageChange,
    onImageRemove,
    isRequired = false,
    label = 'Photo',
    placeholder = 'Upload',
    helperText = 'Drag & Drop / Click to select',
    accept = 'image/*',
    className = '',
    error = false,
    errorText,
  } = props;

  const id = useId();
  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        resizeImageToWidth(
          file,
          (resizedFile) => onImageChange(resizedFile),
          (originalFile) => onImageChange(originalFile)
        );
      }
    },
    [onImageChange]
  );

  const handleRemoveImage = useCallback(() => {
    if (onImageRemove) {
      onImageRemove();
    }
  }, [onImageRemove]);

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label
          htmlFor={id}
          className="text-onlight-text-2 dark:text-ondark-text-2 mb-1 block text-sm"
        >
          {label}
          {isRequired && <span className="text-status-red"> *</span>}
        </label>
      )}

      <div className="relative rounded-md">
        {imagePreview ? (
          <div className="flex justify-center">
            <div className="relative flex w-full items-center justify-center">
              <img
                src={imagePreview}
                alt="Image preview"
                className={clsx(
                  'max-h-40 rounded-lg object-contain',
                  error
                    ? 'border-status-red dark:border-status-red/70 border-2'
                    : 'border-onlight-line-2 dark:border-ondark-line-2 border'
                )}
              />
              {onImageRemove && (
                <div className="absolute -right-2 -top-2">
                  <Button
                    variant="icon"
                    onClick={handleRemoveImage}
                    title="Remove image"
                    className="bg-onlight-bg-1 dark:bg-ondark-bg-2 shadow-md"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative w-full">
            <label
              htmlFor={id}
              className={clsx(
                'flex h-32 w-full cursor-pointer flex-col items-center justify-center space-y-3 rounded-md border-2 border-dashed px-4 py-2 text-sm font-medium focus:outline-none',
                error
                  ? 'border-status-red text-status-red dark:border-status-red/70 dark:text-status-red/90'
                  : 'border-onlight-line-3 text-onlight-text-2 hover:border-onlight-line-3/80 dark:border-ondark-line-3 dark:text-ondark-text-2 dark:hover:border-ondark-line-2'
              )}
            >
              <CameraIcon
                className={clsx(
                  'h-10 w-10',
                  error
                    ? 'text-status-red dark:text-status-red/80'
                    : 'text-onlight-text-3 dark:text-ondark-text-3'
                )}
              />
              <span>{placeholder}</span>
              {helperText && (
                <span className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                  {helperText}
                </span>
              )}
            </label>
            <input
              id={id}
              type="file"
              accept={accept}
              onChange={handleImageChange}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-invalid={error ? 'true' : 'false'}
              required={isRequired}
            />
          </div>
        )}
        {error && (
          <div className="pointer-events-none absolute right-2 top-2 flex items-center">
            <ExclamationCircleIcon
              className="text-status-red h-5 w-5"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      {error && errorText && (
        <div className="mt-2">
          <Alert type="error">{errorText}</Alert>
        </div>
      )}
    </div>
  );
}

export default ImageUpload;
